import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import { useSessionStore } from "@/stores/sessionStore";
import { useCreateFlavorDraftStore } from "@/stores/createFlavorDraftStore";
import { archiveFlavor } from "@/services/flavor_api";
import monitoring from "@/lib/monitoring";
import { serverURL } from "@/utils/serverURL";

// The full_cycle pipeline runs three stages; we surface them as the progress
// steps. The SSE `progress` events carry `stage` ∈ nfood | ndbind | nblock.
const STAGES = [
  { key: "nfood", t: "Analyzing composition", d: "Deriving the molecular profile (N-Food)" },
  { key: "ndbind", t: "Matching descriptors", d: "Binding to flavor-space dimensions" },
  { key: "nblock", t: "Building target profile", d: "Assembling the target flavor vector" },
] as const;

type Phase =
  | { kind: "running"; stage: string | null }
  | { kind: "done" }
  | { kind: "failed"; error: string };

export function GeneratingPage() {
  const navigate = useNavigate();
  const session = useSessionStore((s) => s.session);
  const clearDraft = useCreateFlavorDraftStore((s) => s.clearDraft);
  const flavorId = session?.id ?? null;
  const [phase, setPhase] = useState<Phase>({ kind: "running", stage: null });

  useEffect(() => {
    if (!flavorId) {
      navigate("/", { replace: true });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    const goToWorkbench = () => {
      if (cancelled) return;
      navigate(`/flavors/${flavorId}`, { replace: true });
    };

    // Critical full_cycle failure (worker mid-pipeline). Clean up the broken
    // flavor (archive — best-effort) + report to Sentry, then send the user back
    // to New Flavor. The draft was NOT cleared, so it rehydrates with their
    // files/formulations/specs and shows a generic error by Start Designing.
    const goToNewFlavorFailed = (errorMsg: string) => {
      if (cancelled) return;
      void archiveFlavor(flavorId).catch(() => {});
      monitoring.error(
        "flavor_generation_failed",
        new Error(`full_cycle failed for flavor ${flavorId}: ${errorMsg}`),
      );
      navigate("/flavors/new", { replace: true, state: { generationFailed: true } });
    };

    // Consume the SSE stream via fetch (carries Clerk cookies via credentials),
    // parsing `event:`/`data:` frames. On done/failed → open the workbench (the
    // workbench renders a fallback when the target profile isn't ready).
    (async () => {
      try {
        const res = await fetch(
          serverURL`/me/flavor/${flavorId}/generation/events`,
          { credentials: "include", signal: controller.signal, headers: { Accept: "text/event-stream" } },
        );
        if (!res.ok || !res.body) {
          goToWorkbench();
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done || cancelled) break;
          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split("\n\n");
          buffer = frames.pop() ?? "";
          for (const frame of frames) {
            let event = "message";
            let data = "";
            for (const line of frame.split("\n")) {
              if (line.startsWith("event:")) event = line.slice(6).trim();
              else if (line.startsWith("data:")) data += line.slice(5).trim();
            }
            if (!data) continue;
            let parsed: { status?: string; stage?: string | null; error?: string };
            try {
              parsed = JSON.parse(data);
            } catch {
              continue;
            }
            if (event === "progress") {
              setPhase({ kind: "running", stage: parsed.stage ?? null });
            } else if (event === "done") {
              setPhase({ kind: "done" });
              clearDraft(); // success — discard the New Flavor draft
              goToWorkbench();
              return;
            } else if (event === "failed") {
              const msg = parsed.error ?? "generation_failed";
              setPhase({ kind: "failed", error: msg });
              // Show the failure briefly, then archive + bounce back to New Flavor.
              window.setTimeout(() => goToNewFlavorFailed(msg), 1500);
              return;
            }
          }
        }
      } catch {
        // Aborted on unmount or a network blip — open the workbench as a fallback.
        if (!cancelled) goToWorkbench();
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flavorId]);

  // Resolve the active stage index from the SSE stage (default to the first
  // while we wait for the first progress event).
  const activeIndex =
    phase.kind === "done"
      ? STAGES.length
      : phase.kind === "running"
        ? Math.max(0, STAGES.findIndex((s) => s.key === phase.stage))
        : STAGES.findIndex((s) => s.key === (phase.kind === "failed" ? null : null));
  const current = activeIndex < 0 ? 0 : activeIndex;

  return (
    <div className="generating-root fade-in">
      <div className="generating-card">
        <div className="generating-header">
          <div className="generating-atom">
            <Icon name="atom" size={20} />
          </div>
          <div className="vstack">
            <div className="label">{phase.kind === "failed" ? "Generation failed" : "Generating"}</div>
            <div className="display generating-prompt">
              {session?.label || "New flavor"}
            </div>
          </div>
        </div>

        <div className="panel" style={{ padding: 18 }}>
          {STAGES.map((s, i) => {
            const isDone = i < current || phase.kind === "done";
            const isActive = i === current && phase.kind === "running";
            return (
              <div key={s.key} className="generating-step-row">
                <div
                  className={cn(
                    "generating-step-marker",
                    isDone && "done",
                    isActive && "active",
                    !isDone && !isActive && "pending",
                  )}
                >
                  {isDone ? (
                    <Icon name="check" size={11} />
                  ) : isActive ? (
                    <span className="generating-step-pulse" />
                  ) : (
                    <span className="generating-step-dot" />
                  )}
                </div>
                <div className="generating-step-body">
                  <span
                    className={cn(
                      "generating-step-title",
                      isDone && "done",
                      isActive && "active",
                    )}
                  >
                    {s.t}
                  </span>
                  <span className="generating-step-desc">{s.d}</span>
                </div>
                {isActive && <span className="generating-step-time">running…</span>}
              </div>
            );
          })}
        </div>

        <div className="generating-footer">
          {phase.kind === "failed"
            ? "Something went wrong — taking you back to start over. Your draft is saved."
            : "Deriving your target profile from the R&D pipeline (nfood → ndbind → nblock)."}
        </div>
      </div>
    </div>
  );
}
